import { useEffect, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { StateBlock } from "../components/Cards";
import { displayUserName, UserAvatar } from "../components/UserAvatar";
import { addComment, collectionKey, getComments, getPost, getUser, toggleBookmark, updatePostLike } from "../lib/data";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import { useAuth } from "../lib/auth";
import type { ForumUser, Post } from "../lib/types";

export function PostDetail() {
  const { collectionName = "posts", postId = "" } = useParams();
  const { user, login } = useAuth();
  const { data: post, loading, error } = useAsync(() => getPost(collectionName, postId), [collectionName, postId]);
  const { data: comments } = useAsync(() => getComments(postId), [postId]);
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const { data: author } = useAsync(() => (post?.authorId ? getUser(post.authorId) : Promise.resolve(null)), [post?.authorId]);

  useEffect(() => {
    setSeo({
      title: post ? `${post.title} | My Local Forum` : "Post | My Local Forum",
      description: post?.content?.slice(0, 150) || "Read this local forum post.",
      path: `/posts/${collectionName}/${postId}`
    });
  }, [post, collectionName, postId]);

  if (loading || error || !post) {
    return (
      <section className="page">
        <StateBlock loading={loading} error={error} empty={!loading && !post} />
      </section>
    );
  }

  return (
    <article className="page detail">
      <Link to="/feed" className="text-link">
        Back to feeds
      </Link>
      <h1>{post.title}</h1>
      <Link className="author-row" to={`/users/${encodeURIComponent(post.authorId)}`}>
        <UserAvatar name={displayUserName(author, post.authorEmail || "Forum user")} imageUrl={author?.profilePictureUrl} />
        <span>{displayUserName(author, post.authorEmail || "Forum user")}</span>
      </Link>
      {post.imageUrl ? <img className="hero-image" src={post.imageUrl} alt="" /> : null}
      <p>{post.content}</p>
      <div className="meta">
        <span>{post.likeCount || 0} likes</span>
        <span>{post.commentCount || 0} comments</span>
      </div>
      <div className="action-row">
        {user ? (
          <>
            <button onClick={() => updatePostLike(collectionName, post, user.uid).then(() => window.location.reload())}>
              {post.likes?.includes(user.uid) ? "Unlike" : "Like"}
            </button>
            {profile ? (
              <button
                onClick={() =>
                  toggleBookmark(profile, collectionKey(collectionName, post.id)).then(() => window.location.reload())
                }
              >
                {profile.collectedPosts?.includes(collectionKey(collectionName, post.id)) ? "Remove Bookmark" : "Bookmark"}
              </button>
            ) : null}
          </>
        ) : (
          <button onClick={login}>Sign in to interact</button>
        )}
      </div>

      <section className="comments">
        <h2>Comments</h2>
        {comments?.map((comment) => (
          <article className="comment" key={comment.id}>
            <Link className="message-author" to={`/users/${encodeURIComponent(comment.authorId)}`}>
              <UserAvatar
                name={comment.authorNickname || comment.authorEmail || "Forum user"}
                imageUrl={comment.authorProfilePictureUrl}
                size="sm"
              />
              <strong>{comment.authorNickname || comment.authorEmail || "Forum user"}</strong>
            </Link>
            <p>{comment.content}</p>
          </article>
        ))}
        {user && profile ? <CommentForm post={post} profile={profile} /> : null}
      </section>
    </article>
  );
}

function CommentForm({ post, profile }: { post: Post; profile: ForumUser }) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const content = String(form.get("content") || "").trim();
    if (!content) return;
    await addComment(post, profile, content);
    window.location.reload();
  }

  return (
    <form className="form-grid" onSubmit={submit}>
      <textarea name="content" placeholder="Write a comment" />
      <button className="primary-button" type="submit">
        Comment
      </button>
    </form>
  );
}
